import { useState, useMemo } from "react";
import {
  ShoppingCart,
  CreditCard,
  ExternalLink,
  Loader2,
  Gift,
  Wallet,
  BadgePercent,
} from "lucide-react";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import BreadcrumbComponent from "@/components/BreadcrumbComponent";
import { Typography } from "@/components/ui/typography";
import { Skeleton } from "@/components/ui/skeleton";
import { useGetPriceListQuery } from "@/queries/useGetPriceListQuery";
import { useGetCartCostQuery } from "@/queries/useGetCartCostQuery";
import { useCreatePaymentLinkMutation } from "@/mutations/useCreatePaymentLinkMutation";
import { useGetClientQuery } from "@/queries/useGetClientQuery";
import { useBonuses } from "@/hooks/useBonuses";
import { useDeposits } from "@/hooks/useDeposits";
import { useTickets } from "@/hooks/useTickets";
import { useAuth } from "@/contexts/AuthContext";
import { useClub } from "@/contexts/ClubContext";
import { PriceListItem, CartItem, PaymentListItem } from "@/@types/catalog.types";
import { signPaymentSuccessPayload } from "@/http/paymentApi";
import {
  AKVASTAR_EMAIL_SUFFIX,
  INTERNAL_CLUB_ID,
  PAYMENTS_FAIL,
  PAYMENTS_SUCCESS,
} from "@/utils/const";
import { toast } from "sonner";
import { formatCurrency } from "@/utils/formatters";
import { formatDateWithYear, parseApiDate } from "@/utils/dateFormatters";

const CASHBACK_BANNER_DAYS_BEFORE = 10;
const DAY_MS = 24 * 60 * 60 * 1000;

const normalizePhoneForMatch = (phone: string) => phone.replace(/\D/g, "").replace(/^8/, "7");

const CASHBACK_BANNER_ALWAYS_PHONES_NORMALIZED = (
  import.meta.env.VITE_CASHBACK_ALWAYS_PHONES ?? ""
)
  .split(",")
  .map((p: string) => normalizePhoneForMatch(p.trim()))
  .filter(Boolean);

const isAlwaysShowPhone = (phone: string | undefined | null) => {
  if (!phone || CASHBACK_BANNER_ALWAYS_PHONES_NORMALIZED.length === 0) return false;
  return phone
    .split(",")
    .map((p) => normalizePhoneForMatch(p.trim()))
    .some((p) => p && CASHBACK_BANNER_ALWAYS_PHONES_NORMALIZED.includes(p));
};

const BuyTicket = () => {
  const { selectedClubId, selectedClubName, clubs, setSelectedClub } = useClub();
  const { data: clientData } = useGetClientQuery();
  const { authPhone } = useAuth();
  const [selectedItem, setSelectedItem] = useState<PriceListItem | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Filter clubs based on user permissions (staff can see internal club)
  const filteredClubs = useMemo(() => {
    const email = clientData?.email || "";
    const isStaffEmail = email.endsWith(AKVASTAR_EMAIL_SUFFIX);
    return isStaffEmail ? clubs : clubs.filter((club) => club.id !== INTERNAL_CLUB_ID);
  }, [clubs, clientData]);

  const { data: priceList, isLoading: priceListLoading } = useGetPriceListQuery({
    club_id: selectedClubId,
  });

  const subscriptions = useMemo(() => priceList ?? [], [priceList]);

  // Cart for cost calculation
  const cartItems: CartItem[] = useMemo(() => {
    if (!selectedItem) return [];
    return [{ purchase_id: selectedItem.id, count: 1 }];
  }, [selectedItem]);

  const { data: cartCost, isLoading: cartCostLoading } = useGetCartCostQuery(
    {
      club_id: selectedClubId,
      cart: cartItems,
    },
    !!selectedItem && confirmDialogOpen
  );

  const { mutateAsync: createPaymentLink, isPending: isCreatingPaymentLink } =
    useCreatePaymentLinkMutation();

  const { bonusesData } = useBonuses();
  const { depositsData } = useDeposits();
  const { ticketsData } = useTickets();

  const cashbackEndDate = useMemo(() => {
    const now = Date.now();
    let latest: Date | null = null;
    for (const ticket of ticketsData ?? []) {
      if (ticket.status !== "active") continue;
      if (ticket.type !== "package" && ticket.type !== "membership") continue;
      const endDate = parseApiDate(ticket.end_date);
      if (!endDate) continue;
      if (!latest || endDate.getTime() > latest.getTime()) latest = endDate;
    }
    if (!latest || latest.getTime() < now) return null;

    const alwaysShow = isAlwaysShowPhone(clientData?.phone) || isAlwaysShowPhone(authPhone);
    if (alwaysShow) return latest;

    const daysLeft = Math.ceil((latest.getTime() - now) / DAY_MS);
    if (daysLeft > CASHBACK_BANNER_DAYS_BEFORE) return null;
    return latest;
  }, [ticketsData, clientData, authPhone]);

  // Match user's bonus accounts with cart's allowed payment methods
  const availableBonus = useMemo(() => {
    if (!cartCost) return null;
    for (const bonus of bonusesData) {
      if (bonus.balance <= 0) continue;
      const allowance = cartCost.may_be_payment.find((p) => p.id === bonus.id);
      if (!allowance || allowance.payment_amount <= 0) continue;
      const maxAmount = Math.min(bonus.balance, allowance.payment_amount, cartCost.total_amount);
      if (maxAmount <= 0) continue;
      return { id: bonus.id, name: bonus.name, balance: bonus.balance, maxAmount };
    }
    return null;
  }, [bonusesData, cartCost]);

  const bonusAmount = availableBonus?.maxAmount ?? 0;
  const remainingAfterBonus = cartCost ? Math.max(0, cartCost.total_amount - bonusAmount) : 0;

  // Match user's deposit accounts with cart's allowed payment methods
  const availableDeposit = useMemo(() => {
    if (!cartCost || remainingAfterBonus <= 0) return null;
    const deposits = depositsData ?? [];
    for (const deposit of deposits) {
      if (deposit.balance <= 0) continue;
      const allowance = cartCost.may_be_payment.find((p) => p.id === deposit.id);
      if (!allowance || allowance.payment_amount <= 0) continue;
      const maxAmount = Math.min(deposit.balance, allowance.payment_amount, remainingAfterBonus);
      if (maxAmount <= 0) continue;
      return { id: deposit.id, name: deposit.name, balance: deposit.balance, maxAmount };
    }
    return null;
  }, [depositsData, cartCost, remainingAfterBonus]);

  const depositAmount = availableDeposit?.maxAmount ?? 0;
  const cardAmount = cartCost
    ? Math.max(0, cartCost.total_amount - bonusAmount - depositAmount)
    : 0;

  const handleSelectItem = (item: PriceListItem) => {
    setSelectedItem(item);
    setConfirmDialogOpen(true);
  };

  const handleProceedToPayment = async () => {
    if (!selectedItem || !cartCost) return;

    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    // Open the tab synchronously here so the popup blocker treats it as a
    // user-gesture navigation. We redirect it once the payment link arrives.
    const paymentWindow = isMobile ? null : window.open("about:blank", "_blank");

    try {
      const transactionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // Payment provider requires https — always use the public production origin
      const paymentToken = await signPaymentSuccessPayload({
        title: selectedItem.title,
        amount: cartCost.total_amount,
        club_title: selectedClubName,
        created_at: new Date().toISOString(),
      });
      const buildResultUrl = (path: string) => {
        const url = new URL(path, "https://i.akvastar.ru");
        url.searchParams.set("d", paymentToken);
        return url.toString();
      };
      const successUrlString = buildResultUrl(PAYMENTS_SUCCESS);
      const failUrlString = buildResultUrl(PAYMENTS_FAIL);

      const paymentList: PaymentListItem[] = [];
      if (availableBonus && bonusAmount > 0) {
        paymentList.push({ type: "bonus", id: availableBonus.id, amount: bonusAmount });
      }
      if (availableDeposit && depositAmount > 0) {
        paymentList.push({ type: "deposit", id: availableDeposit.id, amount: depositAmount });
      }
      if (cardAmount > 0) {
        paymentList.push({ type: "card", amount: cardAmount });
      }

      const response = await createPaymentLink({
        transaction_id: transactionId,
        cart: cartItems,
        payment_list: paymentList,
        club_id: selectedClubId,
        success_url: successUrlString,
        fail_url: failUrlString,
      });

      if (response.result && response.data.link) {
        setConfirmDialogOpen(false);
        if (isMobile) {
          window.location.href = response.data.link;
        } else if (paymentWindow) {
          paymentWindow.location.href = response.data.link;
        } else {
          window.location.href = response.data.link;
        }
      } else {
        paymentWindow?.close();
      }
    } catch (error) {
      paymentWindow?.close();
      const responseData = (error as { response?: { data?: unknown } })?.response?.data;
      const message =
        typeof responseData === "object" && responseData !== null && "error" in responseData
          ? String((responseData as { error: unknown }).error)
          : (error as Error)?.message;
      console.error("[payment_link] failed", { error, responseData });
      toast.error("Ошибка создания ссылки на оплату", {
        description: message || "Попробуйте позже или обратитесь к администратору",
      });
    }
  };

  return (
    <div className="flex flex-col p-3">
      <BreadcrumbComponent link={"Купить абонемент"} />
      <div className="mb-6 flex items-center gap-2">
        <ShoppingCart className="h-5 w-5 text-primary" />
        <h2 className="text-xl font-medium text-gray-900">Купить абонемент</h2>
      </div>

      {/* Club selector */}
      <div className="mb-6 flex items-center gap-4">
        <span className="text-sm font-medium text-gray-700">Филиал:</span>
        <Select value={selectedClubId} onValueChange={(value) => setSelectedClub(value)}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Выберите филиал" />
          </SelectTrigger>
          <SelectContent>
            {filteredClubs.map((club) => (
              <SelectItem key={club.id} value={club.id}>
                {club.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {cashbackEndDate && (
        <div className="mb-6 flex items-start gap-3 rounded-lg border border-primary/30 bg-gradient-to-r from-primary/10 to-primary/5 p-4">
          <div className="rounded-full bg-primary/15 p-2">
            <BadgePercent className="h-5 w-5 text-primary" />
          </div>
          <div className="flex flex-col">
            <Typography variant="p" className="text-gray-900">
              Получи кэшбэк 10% при продлении абонемента до{" "}
              {formatDateWithYear(cashbackEndDate)} наличными
            </Typography>
          </div>
        </div>
      )}

      {/* Subscriptions list */}
      <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        {priceListLoading ? (
          [...Array(3)].map((_, index) => (
            <Card key={index} className="overflow-hidden">
              <CardHeader className="border-b bg-muted/50 p-4">
                <Skeleton className="h-6 w-3/4" />
              </CardHeader>
              <CardContent className="space-y-4 p-4">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-10 w-full" />
              </CardContent>
            </Card>
          ))
        ) : subscriptions.length > 0 ? (
          subscriptions.map((item) => (
            <Card key={item.id} className="overflow-hidden">
              <CardHeader className="border-b bg-muted/50 p-4">
                <div className="flex items-center gap-3">
                  <div className="rounded-full bg-primary/10 p-2">
                    <CreditCard className="h-5 w-5 text-primary" />
                  </div>
                  <Typography variant="h3" className="text-lg font-normal text-gray-800">
                    {item.title}
                  </Typography>
                </div>
              </CardHeader>

              <CardContent className="space-y-4 p-4">
                {item.description && (
                  <Typography variant="muted" className="text-sm">
                    {item.description}
                  </Typography>
                )}

                {item.validity && (
                  <div className="flex items-center justify-between text-sm">
                    <Typography variant="muted">Срок действия:</Typography>
                    <Typography variant="p">{item.validity.validity_description}</Typography>
                  </div>
                )}

                <div className="flex items-center justify-between border-t pt-3">
                  <Typography variant="h3" className="text-xl font-semibold text-primary">
                    {formatCurrency(item.price)}
                  </Typography>
                  <Button onClick={() => handleSelectItem(item)}>Купить</Button>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <div className="col-span-full py-8 text-center">
            <ShoppingCart className="mx-auto mb-4 h-12 w-12 text-gray-400" />
            <h3 className="mb-1 text-lg font-medium text-gray-900">Нет доступных абонементов</h3>
            <p className="text-sm text-gray-500">Выберите другой филиал или попробуйте позже</p>
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Оформление покупки</DialogTitle>
            <DialogDescription>Проверьте детали и перейдите к оплате</DialogDescription>
          </DialogHeader>

          {selectedItem && (
            <div className="space-y-4">
              <div className="rounded-lg border bg-gray-50 p-4">
                <div className="flex items-center gap-3">
                  <CreditCard className="h-5 w-5 text-primary" />
                  <span className="font-medium">{selectedItem.title}</span>
                </div>
                {selectedItem.validity && (
                  <p className="mt-2 text-sm text-gray-500">
                    Срок действия: {selectedItem.validity.validity_description}
                  </p>
                )}
              </div>

              {/* Price breakdown */}
              <div className="space-y-2 rounded-lg border p-4">
                {cartCostLoading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-2/3" />
                  </div>
                ) : cartCost ? (
                  <>
                    {cartCost.cart.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span>{item.purchase.title}</span>
                        <span>{formatCurrency(item.price)}</span>
                      </div>
                    ))}
                    {cartCost.total_discount > 0 && (
                      <div className="flex justify-between text-sm text-green-600">
                        <span>Скидка</span>
                        <span>-{formatCurrency(cartCost.total_discount)}</span>
                      </div>
                    )}
                    {cartCost.promotions?.map((promo, idx) => (
                      <div key={idx} className="flex justify-between text-sm text-green-600">
                        <span>{promo.title}</span>
                        <span>-{formatCurrency(promo.amount)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between border-t pt-2 font-semibold">
                      <span>Итого к оплате</span>
                      <span className="text-primary">{formatCurrency(cartCost.total_amount)}</span>
                    </div>

                    {(bonusAmount > 0 || depositAmount > 0) && (
                      <div className="space-y-2 border-t pt-3">
                        {availableBonus && bonusAmount > 0 && (
                          <div className="flex items-center gap-2 text-sm">
                            <Gift className="h-4 w-4 shrink-0 text-primary" />
                            <span className="font-medium">{availableBonus.name}</span>
                            <span className="ml-auto text-xs text-muted-foreground">
                              баланс {availableBonus.balance.toLocaleString("ru-RU")}
                            </span>
                          </div>
                        )}
                        {availableDeposit && depositAmount > 0 && (
                          <div className="flex items-center gap-2 text-sm">
                            <Wallet className="h-4 w-4 shrink-0 text-primary" />
                            <span className="font-medium">{availableDeposit.name}</span>
                            <span className="ml-auto text-xs text-muted-foreground">
                              баланс {formatCurrency(availableDeposit.balance)}
                            </span>
                          </div>
                        )}
                        <div className="space-y-1 rounded-md bg-muted/50 p-2 text-sm">
                          {bonusAmount > 0 && (
                            <div className="flex justify-between text-green-600">
                              <span>Списано бонусами</span>
                              <span>-{formatCurrency(bonusAmount)}</span>
                            </div>
                          )}
                          {depositAmount > 0 && (
                            <div className="flex justify-between text-green-600">
                              <span>Списано с лицевого счёта</span>
                              <span>-{formatCurrency(depositAmount)}</span>
                            </div>
                          )}
                          <div className="flex justify-between font-medium">
                            <span>К оплате картой</span>
                            <span>{formatCurrency(cardAmount)}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex justify-between font-semibold">
                    <span>Итого к оплате</span>
                    <span className="text-primary">{formatCurrency(selectedItem.price)}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmDialogOpen(false)}>
              Отмена
            </Button>
            <Button
              onClick={handleProceedToPayment}
              disabled={isCreatingPaymentLink || cartCostLoading}
            >
              {isCreatingPaymentLink ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Создание ссылки...
                </>
              ) : (
                <>
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Перейти к оплате
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BuyTicket;
